/**
 * Governance API Service
 * Reutiliza o mesmo padrão AZURE do projeto (src/services/api.js)
 */
import { AZURE } from './api';

/**
 * GET /api/governance/overview
 * Retorna métricas consolidadas do dashboard
 */
export const getGovernanceOverview = async (environment = 'production') => {
  try {
    const { data } = await AZURE.get('/governance/overview', {
      params: { environment }
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching overview:', error);
    throw error;
  }
};

/**
 * GET /api/governance/agents
 * Lista os 14 agentes com métricas runtime
 */
export const getGovernanceAgents = async (classFilter = null, environment = 'production') => {
  try {
    const params = { environment };
    if (classFilter) params.class = classFilter;
    
    const { data } = await AZURE.get('/governance/agents', { params });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching agents:', error);
    throw error;
  }
};

/**
 * POST /api/governance/agents/:name/enable
 * Habilita um agente
 */
export const enableAgent = async (agentName, environment = 'production') => {
  try {
    const { data } = await AZURE.post(
      `/governance/agents/${agentName}/enable`,
      {},
      { params: { environment } }
    );
    return data;
  } catch (error) {
    console.error(`[GovernanceService] Error enabling agent ${agentName}:`, error);
    throw error;
  }
};

/**
 * POST /api/governance/agents/:name/disable
 * Desabilita um agente (requer reason)
 */
export const disableAgent = async (agentName, reason, environment = 'production') => {
  try {
    const { data } = await AZURE.post(
      `/governance/agents/${agentName}/disable`,
      { reason },
      { params: { environment } }
    );
    return data;
  } catch (error) {
    console.error(`[GovernanceService] Error disabling agent ${agentName}:`, error);
    throw error;
  }
};

/**
 * GET /api/governance/agents/flags
 * Status de enable/disable de todos os agentes
 */
export const getAgentFlags = async (environment = 'production') => {
  try {
    const { data } = await AZURE.get('/governance/agents/flags', {
      params: { environment }
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching agent flags:', error);
    throw error;
  }
};

/**
 * PATCH /api/governance/agents/:agentName/config
 * Update agent config (model, max_tokens, timeout_ms, router_mode)
 */
export const updateAgentConfig = async (agentName, config) => {
  try {
    const { data } = await AZURE.patch(`/governance/agents/${agentName}/config`, config);
    return data;
  } catch (error) {
    console.error(`[GovernanceService] Error updating agent config ${agentName}:`, error);
    throw error;
  }
};

/**
 * DELETE /api/governance/agents/:agentName/delete
 * Delete agent from Azure AI Foundry
 */
export const deleteAgentFromFoundry = async (agentName) => {
  try {
    const { data } = await AZURE.delete(`/governance/agents/${agentName}/delete`);
    return data;
  } catch (error) {
    console.error(`[GovernanceService] Error deleting agent ${agentName}:`, error);
    throw error;
  }
};

/**
 * POST /api/governance/agents/sync-foundry
 * Sync agent list from Azure AI Foundry
 */
export const syncAgentsFromFoundry = async () => {
  try {
    const { data } = await AZURE.post('/governance/agents/sync-foundry');
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error syncing from Foundry:', error);
    throw error;
  }
};

/**
 * GET /api/governance/budget-forecast
 * Budget utilization + end-of-month projection
 */
export const getGovernanceBudgetForecast = async (environment = 'production') => {
  try {
    const { data } = await AZURE.get('/governance/budget-forecast', {
      params: { environment }
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching budget forecast:', error);
    throw error;
  }
};

/**
 * GET /api/governance/costs
 * Dados de custo para gráficos
 * @param {string} view - 'by_agent' | 'by_day' | 'burn_rate'
 */
export const getGovernanceCosts = async (view = 'by_agent', environment = 'production') => {
  try {
    const { data } = await AZURE.get('/governance/costs', {
      params: { view, environment }
    });
    // Backend returns {success, data: {view, data: [...], limits}}
    // Unwrap so callers get {data: [...], limits, view} directly
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching costs:', error);
    throw error;
  }
};

/**
 * GET /api/governance/incidents
 * Feed de incidentes com filtros
 */
export const getGovernanceIncidents = async (filters = {}, environment = 'production') => {
  try {
    const params = { environment, ...filters };
    const { data } = await AZURE.get('/governance/incidents', { params });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching incidents:', error);
    throw error;
  }
};

/**
 * POST /api/governance/builder/chat
 * Envia mensagem ao Builder e recebe resposta estruturada
 */
export const sendBuilderMessage = async (message, context = {}) => {
  try {
    const { data } = await AZURE.post('/governance/builder/chat', {
      message,
      context
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error sending builder message:', error);
    throw error;
  }
};

/**
 * POST /api/governance/builder/tasks
 * Gera tarefas a partir de um plano
 */
export const generateTasks = async (planId = null, itemIndices = []) => {
  try {
    const { data } = await AZURE.post('/governance/builder/tasks', {
      plan_id: planId,
      item_indices: itemIndices
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error generating tasks:', error);
    throw error;
  }
};

/**
 * PUT /api/governance/builder/tasks/:taskId/status
 * Atualiza status de uma tarefa
 */
export const updateTaskStatus = async (taskId, status, actualCostUsd = null) => {
  try {
    const { data } = await AZURE.put(`/governance/builder/tasks/${taskId}/status`, {
      status,
      actual_cost_usd: actualCostUsd
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error updating task status:', error);
    throw error;
  }
};

/**
 * POST /api/governance/builder/pm/decide
 * PM Agent decide prioridades (structured decisions com reason_codes)
 */
export const pmDecide = async (planItems, context) => {
  try {
    const { data } = await AZURE.post('/governance/builder/pm/decide', {
      plan_items: planItems,
      context
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error in PM decide:', error);
    throw error;
  }
};

/**
 * GET /api/governance/builder/pm/reason-codes
 * Retorna dicionário de reason codes para tooltips/legendas
 */
export const getPmReasonCodes = async () => {
  try {
    const { data } = await AZURE.get('/governance/builder/pm/reason-codes');
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching PM reason codes:', error);
    throw error;
  }
};

/**
 * GET /api/governance/reconciliation
 * Reconciliação de custos: projetado vs realizado
 */
export const getGovernanceReconciliation = async (environment = 'production', windowDays = 30) => {
  try {
    const { data } = await AZURE.get('/governance/reconciliation', {
      params: { environment, window_days: windowDays }
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching reconciliation:', error);
    throw error;
  }
};

/**
 * GET /api/governance/reconciliation/summary
 * Resumo rápido de custos reais (para Planner sidebar)
 */
export const getReconciliationSummary = async (environment = 'production', windowDays = 30) => {
  try {
    const { data } = await AZURE.get('/governance/reconciliation/summary', {
      params: { environment, window_days: windowDays }
    });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching reconciliation summary:', error);
    throw error;
  }
};

/**
 * GET /api/governance/planner/plans
 * Lista todos os planos do Planner
 */
export const listPlannerPlans = async (statusFilter = null) => {
  try {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    
    const { data } = await AZURE.get('/governance/planner/plans', { params });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching planner plans:', error);
    throw error;
  }
};

/**
 * GET /api/governance/planner/snapshot
 * Retorna snapshot do sistema com canvas estruturado
 */
export const getPlannerSnapshot = async (planId = null, save = false) => {
  try {
    const params = {};
    if (planId) params.plan_id = planId;
    if (save) params.save = 'true';
    
    const { data } = await AZURE.get('/governance/planner/snapshot', { params });
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching planner snapshot:', error);
    throw error;
  }
};

/**
 * POST /api/governance/research
 * Executa pesquisa governada com AS_GOV_RESEARCHER agent
 */
export const researchGovernance = async (query, domains = null, mode = 'docs') => {
  try {
    const payload = { query, mode };
    if (domains && domains.length > 0) {
      payload.domains = domains;
    }
    
    const { data } = await AZURE.post('/governance/research', payload);
    return data;
  } catch (error) {
    console.error('[GovernanceService] Error executing research:', error);
    throw error;
  }
};

// ============================================================================
// OPS ENDPOINTS (Worker + Ingestion Pipeline)
// ============================================================================

/**
 * GET /api/ops/worker-status
 * Worker health, queue depth, last activity
 */
export const getOpsWorkerStatus = async () => {
  try {
    const { data } = await AZURE.get('/ops/worker-status');
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching worker status:', error);
    throw error;
  }
};

/**
 * GET /api/ops/ingestion-dashboard
 * Ingestion pipeline metrics (runs, latency, validation rates)
 */
export const getOpsIngestionDashboard = async () => {
  try {
    const { data } = await AZURE.get('/ops/ingestion-dashboard');
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching ingestion dashboard:', error);
    throw error;
  }
};

/**
 * GET /api/ops/slo-dashboard
 * All 6 SLOs with meeting/failing status
 */
export const getOpsSLODashboard = async () => {
  try {
    const { data } = await AZURE.get('/ops/slo-dashboard');
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching SLO dashboard:', error);
    throw error;
  }
};

/**
 * GET /api/ops/alerts/active
 * Active alerts with severity breakdown
 */
export const getOpsAlerts = async (params = {}) => {
  try {
    const { data } = await AZURE.get('/ops/alerts/active', { params });
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching alerts:', error);
    throw error;
  }
};

/**
 * GET /api/ops/telemetry/latency
 * Top slow endpoints
 */
export const getOpsTelemetry = async (params = {}) => {
  try {
    const { data } = await AZURE.get('/ops/telemetry/latency', { params });
    return data?.data || data;
  } catch (error) {
    console.error('[GovernanceService] Error fetching telemetry:', error);
    throw error;
  }
};

/**
 * GET /api/internal/monitoring/overview
 * Visão geral real da plataforma VUKZ (Fase 1 Observabilidade)
 * Auth: X-Ops-Api-Key ou super_admin JWT
 */
export const getMonitoringOverview = async () => {
  try {
    const { data } = await AZURE.get('/internal/monitoring/overview');
    return data?.data !== undefined ? data : data;
  } catch (error) {
    console.error('[MonitoringService] Error fetching overview:', error);
    throw error;
  }
};

/**
 * GET /api/internal/monitoring/services
 * Lista de serviços com último status
 */
export const getMonitoringServices = async () => {
  try {
    const { data } = await AZURE.get('/internal/monitoring/services');
    return data?.data || [];
  } catch (error) {
    console.error('[MonitoringService] Error fetching services:', error);
    throw error;
  }
};

/**
 * GET /api/internal/monitoring/healthchecks?resource=<name>&hours=<n>
 */
export const getMonitoringHealthchecks = async (params = {}) => {
  try {
    const { data } = await AZURE.get('/internal/monitoring/healthchecks', { params });
    return data?.data || [];
  } catch (error) {
    console.error('[MonitoringService] Error fetching healthchecks:', error);
    throw error;
  }
};

/**
 * GET /api/internal/monitoring/observability
 * Estado real da stack de observabilidade
 */
export const getMonitoringObservability = async () => {
  try {
    const { data } = await AZURE.get('/internal/monitoring/observability');
    return data?.data || data;
  } catch (error) {
    console.error('[MonitoringService] Error fetching observability state:', error);
    throw error;
  }
};

